import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import {
  buildCommerceAuditColumns,
  formatCommerceDateOnly,
  formatCommerceDateTime,
  useCommerceUserTimezone,
} from '../utils/commerceListDateUtils';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import {
  parseCustomerNameLocalesRoot,
  pickOrderCustomerNameSnapshot,
  resolveCustomerDisplayName,
  resolveLocalizedEntityName
} from '../constants/commerceNameLocales';
import { computeBrokenPackLineAmount } from './wpa/wpaProductUtils';

const { Title, Text } = Typography;

/** 表格欄位：截斷過長內容，hover 以 Tooltip 顯示全文（避免溢出蓋到下一欄） */
function renderEllipsisTooltip(fullText, node) {
  const text = fullText == null ? '' : String(fullText);
  if (!text || text === '-') {
    return node != null ? node : '-';
  }
  const ellipsisStyle = {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    verticalAlign: 'top'
  };
  const inner = React.isValidElement(node)
    ? React.cloneElement(node, {
        style: { ...(node.props.style || {}), ...ellipsisStyle, marginInlineEnd: 0 }
      })
    : (
      <span style={ellipsisStyle}>{node}</span>
      );
  return (
    <Tooltip title={text} placement="topLeft">
      <div style={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>{inner}</div>
    </Tooltip>
  );
}

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Confirmed', value: 'confirmed' },
  { label: 'Processing', value: 'processing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Cancelled', value: 'cancelled' }
];

const STATUS_COLOR_MAP = {
  pending: 'orange',
  confirmed: 'blue',
  processing: 'cyan',
  completed: 'green',
  cancelled: 'red'
};

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_TO_JS = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };

const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '') ?? null;

/** v2：存主檔 name + nameLocales，下拉標籤依介面語系即時解析（v1 僅存已翻譯字串，無法換語系） */
const RECENT_PRODUCTS_STORAGE_KEY = 'commerce_sales_order_recent_products_v2';

function readRecentProductEntries() {
  try {
    const raw = localStorage.getItem(RECENT_PRODUCTS_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr.filter((x) => x && x.productRetailerId);
  } catch {
    return [];
  }
}

function writeRecentProductEntries(entries) {
  localStorage.setItem(RECENT_PRODUCTS_STORAGE_KEY, JSON.stringify(entries.slice(0, 40)));
}

function productFromApiRow(row) {
  if (!row || typeof row !== 'object') return null;
  const id = pick(row.productRetailerId, row.productRetailer_id);
  if (!id) return null;
  const rawQpu = pick(row.qtyPerUnit, row.qty_per_unit);
  const rawMoq = pick(row.minOrderQuantity, row.min_order_quantity);
  return {
    productRetailerId: String(id),
    productIdGuid: pick(row.id, row.Id) || null,
    name: row.name || '',
    nameLocales: pick(row.nameLocales, row.name_locales),
    price: row.price != null ? Number(row.price) : null,
    currency: row.currency || 'HKD',
    qtyPerUnit: rawQpu != null ? Number(rawQpu) : null,
    minOrderQuantity: rawMoq != null ? Number(rawMoq) : null,
  };
}

function joinAddr(...parts) {
  const s = parts.filter(Boolean).map(String).map((x) => x.trim()).filter(Boolean);
  return s.length ? s.join('\n') : '';
}

/** 將 API（camelCase / 或 snake）轉成畫面用的 customerInfo（含 delivery_mon 等鍵） */
function mapCustomerFromApi(raw, uiLocale) {
  if (!raw || typeof raw !== 'object') return null;
  const id = pick(raw.id, raw.Id);
  const customer_no = pick(raw.customerNo, raw.customer_no);
  const branch_no = pick(raw.branchNo, raw.branch_no) || '';
  const n1 = pick(raw.name_1, raw.name1, raw.Name1);
  const n2 = pick(raw.name_2, raw.name2, raw.Name2);
  const n3 = pick(raw.name_3, raw.name3, raw.Name3);
  const nl = parseCustomerNameLocalesRoot(pick(raw.name_locales, raw.nameLocales, raw.NameLocales));
  const displayFromApi = pick(raw.displayName, raw.DisplayName);
  const customer_display_ui = displayFromApi || resolveCustomerDisplayName(n1, n2, n3, nl, uiLocale);
  const legacyCn = pick(raw.nameCn, raw.name_cn_1, raw.nameCN1, raw.nameCn1)
    || [raw.nameCn1, raw.nameCn2, raw.nameCn3, raw.name_cn_1, raw.name_cn_2, raw.name_cn_3]
      .filter((x) => x != null && String(x).trim() !== '')
      .map((x) => String(x).trim())
      .join(' ');
  const legacyEn = pick(raw.nameEn, raw.name_en_1, raw.nameEN1, raw.nameEn1)
    || [raw.nameEn1, raw.nameEn2, raw.nameEn3, raw.name_en_1, raw.name_en_2, raw.name_en_3]
      .filter((x) => x != null && String(x).trim() !== '')
      .map((x) => String(x).trim())
      .join(' ');
  const nameCn = (legacyCn && legacyCn.trim()) || resolveCustomerDisplayName(n1, n2, n3, nl, 'zh-TC');
  const nameEn = (legacyEn && legacyEn.trim()) || resolveCustomerDisplayName(n1, n2, n3, nl, 'en');
  const billingCn = joinAddr(
    raw.billingAddressCn1, raw.billingAddressCn2, raw.billingAddressCn3,
    raw.billing_address_cn_1, raw.billing_address_cn_2, raw.billing_address_cn_3
  );
  const billingEn = joinAddr(
    raw.billingAddressEn1, raw.billingAddressEn2, raw.billingAddressEn3,
    raw.billing_address_en_1, raw.billing_address_en_2, raw.billing_address_en_3
  );
  const deliveryCn = joinAddr(
    raw.deliveryAddressCn1, raw.deliveryAddressCn2, raw.deliveryAddressCn3,
    raw.delivery_address_cn_1, raw.delivery_address_cn_2, raw.delivery_address_cn_3
  );
  const deliveryEn = joinAddr(
    raw.deliveryAddressEn1, raw.deliveryAddressEn2, raw.deliveryAddressEn3,
    raw.delivery_address_en_1, raw.delivery_address_en_2, raw.delivery_address_en_3
  );
  const billing_address = billingCn || billingEn || '';
  const delivery_address = deliveryCn || deliveryEn || '';
  const dm = !!(pick(raw.deliveryDayMon, raw.delivery_day_mon, raw.DeliveryDayMon));
  const dtu = !!(pick(raw.deliveryDayTue, raw.delivery_day_tue, raw.DeliveryDayTue));
  const dw = !!(pick(raw.deliveryDayWed, raw.delivery_day_wed, raw.DeliveryDayWed));
  const dth = !!(pick(raw.deliveryDayThu, raw.delivery_day_thu, raw.DeliveryDayThu));
  const df = !!(pick(raw.deliveryDayFri, raw.delivery_day_fri, raw.DeliveryDayFri));
  const dsat = !!(pick(raw.deliveryDaySat, raw.delivery_day_sat, raw.DeliveryDaySat));
  const dsun = !!(pick(raw.deliveryDaySun, raw.delivery_day_sun, raw.DeliveryDaySun));
  const hol = !!(pick(raw.deliveryDayHoliday, raw.delivery_day_holiday, raw.DeliveryDayHoliday));
  return {
    id: id != null ? String(id) : undefined,
    customer_no,
    branch_no,
    customer_display_ui: customer_display_ui || '',
    company_name_cn: nameCn || '',
    company_name_en: nameEn || '',
    billing_address,
    delivery_address,
    contact_name: pick(raw.contactName, raw.contact_name) || '',
    contact_tel: pick(raw.tel, raw.contact_tel) || '',
    salesman_name: pick(raw.salesmanName, raw.salesman_name) || '',
    delivery_mon: dm,
    delivery_tue: dtu,
    delivery_wed: dw,
    delivery_thu: dth,
    delivery_fri: df,
    delivery_sat: dsat,
    delivery_sun: dsun,
    is_holiday: hol,
    payment_term: pick(raw.paymentTerm, raw.payment_term, raw.PaymentTerm) || ''
  };
}

function mapOrderLineFromApi(d, idx) {
  if (!d || typeof d !== 'object') return null;
  const id = pick(d.id, d.Id);
  const seq = pick(d.sequenceNo, d.SequenceNo, d.seq, d.Seq) ?? idx + 1;
  const unit = Number(pick(d.unitPrice, d.unit_price) ?? 0);
  const qty = Number(pick(d.quantity, d.Quantity) ?? 0);
  const amt = pick(d.amount, d.Amount);
  const rawQpu = pick(d.qtyPerUnit, d.qty_per_unit);
  const rawMoq = pick(d.minOrderQuantity, d.min_order_quantity);
  const qty_per_unit = rawQpu != null ? Number(rawQpu) : null;
  const min_order_quantity = rawMoq != null ? Number(rawMoq) : null;
  const product_id_guid = pick(d.productId, d.ProductId) || null;
  return {
    key: id != null ? String(id) : `line_${idx}`,
    id: id != null ? id : undefined,
    seq,
    item_no: pick(d.itemNo, d.item_no) || '',
    product_name: pick(d.productName, d.product_name) || '',
    unit_price: unit,
    quantity: qty,
    unit: pick(d.unit, d.Unit) || '',
    qty_per_unit,
    min_order_quantity,
    product_id_guid,
    amount: amt != null ? Number(amt) : computeBrokenPackLineAmount(qty, unit, qty_per_unit, min_order_quantity),
    remark: pick(d.remark, d.Remark) || ''
  };
}

function enrichCustomerWithOrderSnapshot(mapped, order, uiLocale) {
  if (!mapped || !order) return mapped;
  const cnSnap = mapped.company_name_cn || pick(order.companyNameCn, order.company_name_cn) || '';
  const enSnap = mapped.company_name_en || pick(order.companyNameEn, order.company_name_en) || '';
  return {
    ...mapped,
    company_name_cn: cnSnap,
    company_name_en: enSnap,
    customer_display_ui:
      mapped.customer_display_ui ||
      pickOrderCustomerNameSnapshot(cnSnap, enSnap, uiLocale),
    billing_address: mapped.billing_address || pick(order.billingAddress, order.billing_address) || '',
    delivery_address: mapped.delivery_address || pick(order.deliveryAddress, order.delivery_address) || '',
    contact_name: mapped.contact_name || pick(order.contactName, order.contact_name) || '',
    contact_tel: mapped.contact_tel || pick(order.contactTel, order.contact_tel) || '',
    salesman_name: mapped.salesman_name || pick(order.salesmanName, order.salesman_name) || ''
  };
}

const CommerceSalesOrdersPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [branchNoFilter, setBranchNoFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('view');
  const [saving, setSaving] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(undefined);
  const [orderCompanyId, setOrderCompanyId] = useState(undefined);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearchInput, setPickerSearchInput] = useState('');
  const [pickerKeyword, setPickerKeyword] = useState('');
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerPageSize, setPickerPageSize] = useState(20);
  const [pickerTotal, setPickerTotal] = useState(0);
  const [pickerRows, setPickerRows] = useState([]);
  const [recentProductsTick, setRecentProductsTick] = useState(0);
  const [fallbackQuickProducts, setFallbackQuickProducts] = useState([]);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productPickerLineKey, setProductPickerLineKey] = useState(null);
  const [productPickerLoading, setProductPickerLoading] = useState(false);
  const [productPickerSearchInput, setProductPickerSearchInput] = useState('');
  const [productPickerKeyword, setProductPickerKeyword] = useState('');
  const [productPickerPage, setProductPickerPage] = useState(1);
  const [productPickerPageSize, setProductPickerPageSize] = useState(20);
  const [productPickerTotal, setProductPickerTotal] = useState(0);
  const [productPickerRows, setProductPickerRows] = useState([]);
  const [orderLines, setOrderLines] = useState([]);
  const [headerFields, setHeaderFields] = useState({
    order_date: dayjs(),
    delivery_date: null,
    purchase_order_no: '',
    remark: '',
    payment_term: ''
  });
  const [paymentTermOptions, setPaymentTermOptions] = useState([
    'COD', '30日數期月結', '60日數期月結'
  ]);

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const effectiveOrderCompanyId = useMemo(() => {
    if (!isTenantAdmin) return null;
    return orderCompanyId || pick(currentOrder?.companyId, currentOrder?.company_id) || null;
  }, [isTenantAdmin, orderCompanyId, currentOrder]);

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    const isFormData = options.body instanceof FormData;
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  const extractResponseError = async (response) => {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const data = JSON.parse(text);
      return data?.error || data?.detail || data?.message || text;
    } catch {
      return text;
    }
  };

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('search', keyword.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (branchNoFilter.trim()) params.set('branchNo', branchNoFilter.trim());
      if (companyFilter) params.set('companyId', companyFilter);
      if (dateRange && dateRange[0]) params.set('dateFrom', dateRange[0].format('YYYY-MM-DD'));
      if (dateRange && dateRange[1]) params.set('dateTo', dateRange[1].format('YYYY-MM-DD'));
      params.set('page', String(ordersPage));
      params.set('pageSize', String(ordersPageSize));
      const response = await fetchWithAuth(`/api/commerce/sales-orders?${params.toString()}`);
      if (!response.ok) throw new Error(await extractResponseError(response));
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setOrders(items);
      setOrdersTotal(total);
    } catch (err) {
      message.error(`${t('commerceSalesOrders.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, keyword, statusFilter, branchNoFilter, companyFilter, dateRange, ordersPage, ordersPageSize, t]);

  const loadCompaniesForSelect = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('載入公司列表失敗:', err);
    }
  }, [fetchWithAuth]);

  const loadPaymentTerms = useCallback(async () => {
    try {
      const params = companyFilter ? `?companyId=${companyFilter}` : '';
      const res = await fetchWithAuth(`/api/commerce/payment-terms${params}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const defaults = ['COD', '30日數期月結', '60日數期月結'];
        const merged = [...new Set([...defaults, ...data])];
        setPaymentTermOptions(merged);
      }
    } catch (err) {
      console.warn('載入付款條款失敗:', err);
    }
  }, [fetchWithAuth, companyFilter]);

  const fetchPickerCustomers = useCallback(async () => {
    setPickerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pickerPage));
      params.set('pageSize', String(pickerPageSize));
      if (pickerKeyword.trim()) params.set('search', pickerKeyword.trim());
      if (isTenantAdmin && effectiveOrderCompanyId) params.set('companyId', String(effectiveOrderCompanyId));
      const res = await fetchWithAuth(`/api/commerce/customers-for-select?${params.toString()}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setPickerRows(items);
      setPickerTotal(total);
    } catch (err) {
      message.error(`${t('commerceSalesOrders.customerPickerLoadFailed')}: ${err.message}`);
      setPickerRows([]);
      setPickerTotal(0);
    } finally {
      setPickerLoading(false);
    }
  }, [fetchWithAuth, pickerPage, pickerPageSize, pickerKeyword, effectiveOrderCompanyId, isTenantAdmin, t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadCompaniesForSelect();
    loadPaymentTerms();
  }, [loadCompaniesForSelect, loadPaymentTerms]);

  useEffect(() => {
    if (!customerPickerOpen) return;
    fetchPickerCustomers();
  }, [customerPickerOpen, fetchPickerCustomers]);

  useEffect(() => {
    if (!drawerOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        params.set('page', '1');
        params.set('pageSize', '10');
        if (isTenantAdmin && effectiveOrderCompanyId) params.set('companyId', String(effectiveOrderCompanyId));
        const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        const normalized = items.map(productFromApiRow).filter(Boolean);
        if (!cancelled) setFallbackQuickProducts(normalized);
      } catch {
        if (!cancelled) setFallbackQuickProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [drawerOpen, isTenantAdmin, effectiveOrderCompanyId, fetchWithAuth]);

  const fetchProductPickerRows = useCallback(async () => {
    setProductPickerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(productPickerPage));
      params.set('pageSize', String(productPickerPageSize));
      if (productPickerKeyword.trim()) params.set('search', productPickerKeyword.trim());
      if (isTenantAdmin && effectiveOrderCompanyId) params.set('companyId', String(effectiveOrderCompanyId));
      const res = await fetchWithAuth(`/api/commerce/products?${params.toString()}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const total = typeof data?.total === 'number' ? data.total : items.length;
      setProductPickerRows(items);
      setProductPickerTotal(total);
    } catch (err) {
      message.error(`${t('commerceSalesOrders.productPickerLoadFailed')}: ${err.message}`);
      setProductPickerRows([]);
      setProductPickerTotal(0);
    } finally {
      setProductPickerLoading(false);
    }
  }, [fetchWithAuth, productPickerPage, productPickerPageSize, productPickerKeyword, isTenantAdmin, effectiveOrderCompanyId, t]);

  useEffect(() => {
    if (!productPickerOpen) return;
    fetchProductPickerRows();
  }, [productPickerOpen, fetchProductPickerRows]);

  const pushRecentProductEntry = useCallback((p) => {
    if (!p?.productRetailerId) return;
    const cur = readRecentProductEntries().filter((x) => x.productRetailerId !== p.productRetailerId);
    cur.unshift({
      productRetailerId: p.productRetailerId,
      name: p.name || '',
      nameLocales: p.nameLocales ?? p.name_locales ?? null,
      price: p.price,
      currency: p.currency || 'HKD'
    });
    writeRecentProductEntries(cur);
    setRecentProductsTick((n) => n + 1);
  }, []);

  const applyProductToLine = useCallback((lineKey, product) => {
    if (!product?.productRetailerId) return;
    pushRecentProductEntry(product);
    setOrderLines((prev) => prev.map((line) => {
      if (line.key !== lineKey) return line;
      const item_no = product.productRetailerId;
      const product_name =
        resolveLocalizedEntityName(product.name, product.nameLocales ?? product.name_locales, currentLanguage)
        || product.name
        || '';
      let unit_price = line.unit_price;
      if (unit_price == null || Number(unit_price) === 0) {
        unit_price = product.price != null ? product.price : 0;
      }
      const quantity = Number(line.quantity) || 0;
      const qty_per_unit = product.qtyPerUnit ?? null;
      const min_order_quantity = product.minOrderQuantity ?? null;
      const product_id_guid = product.productIdGuid ?? null;
      return {
        ...line,
        item_no,
        product_name,
        unit_price: Number(unit_price),
        qty_per_unit,
        min_order_quantity,
        product_id_guid,
        amount: computeBrokenPackLineAmount(quantity, Number(unit_price), qty_per_unit, min_order_quantity)
      };
    }));
  }, [pushRecentProductEntry, currentLanguage]);

  const baseQuickProducts = useMemo(() => {
    const recent = readRecentProductEntries();
    const seen = new Set();
    const out = [];
    for (const p of [...recent, ...fallbackQuickProducts]) {
      const id = p?.productRetailerId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(p);
      if (out.length >= 10) break;
    }
    return out;
  }, [recentProductsTick, fallbackQuickProducts]);

  const openLineProductPicker = useCallback((lineKey) => {
    if (isTenantAdmin && !effectiveOrderCompanyId) {
      message.warning(t('commerceSalesOrders.selectCompanyFirst'));
      return;
    }
    setProductPickerLineKey(lineKey);
    setProductPickerPage(1);
    setProductPickerKeyword('');
    setProductPickerSearchInput('');
    setProductPickerOpen(true);
  }, [isTenantAdmin, effectiveOrderCompanyId, t]);

  const applyPickedProductFromModal = useCallback((row) => {
    const p = productFromApiRow(row);
    if (!p || !productPickerLineKey) return;
    applyProductToLine(productPickerLineKey, p);
    setProductPickerOpen(false);
    setProductPickerLineKey(null);
  }, [productPickerLineKey, applyProductToLine]);

  const loadOrderDetail = useCallback(async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/sales-orders/${id}`);
      if (!response.ok) throw new Error(await extractResponseError(response));
      const data = await response.json();
      const order = data.order || data;
      const customer = data.customer || null;
      setCurrentOrder(order);
      setOrderCompanyId(pick(order.companyId, order.company_id) || undefined);
      let mapped = customer ? mapCustomerFromApi(customer, currentLanguage) : null;
      if (!mapped && pick(order.customerId, order.customer_id)) {
        const cn0 = pick(order.companyNameCn, order.company_name_cn) || '';
        const en0 = pick(order.companyNameEn, order.company_name_en) || '';
        mapped = {
          id: String(pick(order.customerId, order.customer_id)),
          customer_no: pick(order.customerNo, order.customer_no),
          branch_no: pick(order.branchNo, order.branch_no) || '',
          company_name_cn: cn0,
          company_name_en: en0,
          customer_display_ui: pickOrderCustomerNameSnapshot(cn0, en0, currentLanguage),
          billing_address: pick(order.billingAddress, order.billing_address) || '',
          delivery_address: pick(order.deliveryAddress, order.delivery_address) || '',
          contact_name: pick(order.contactName, order.contact_name) || '',
          contact_tel: pick(order.contactTel, order.contact_tel) || '',
          salesman_name: pick(order.salesmanName, order.salesman_name) || '',
          delivery_mon: false,
          delivery_tue: false,
          delivery_wed: false,
          delivery_thu: false,
          delivery_fri: false,
          delivery_sat: false,
          delivery_sun: false,
          is_holiday: false
        };
      }
      mapped = enrichCustomerWithOrderSnapshot(mapped, order, currentLanguage);
      setCustomerInfo(mapped);
      const custId = pick(order.customerId, order.customer_id);
      setSelectedCustomerId(custId != null ? String(custId) : undefined);
      const rawDetails = order.details || order.Details || [];
      setOrderLines(rawDetails.map((d, idx) => mapOrderLineFromApi(d, idx)).filter(Boolean));
      setHeaderFields({
        order_date: (() => {
          const od = pick(order.orderDate, order.order_date);
          return od ? dayjs(od) : dayjs();
        })(),
        delivery_date: (() => {
          const dd = pick(order.deliveryDate, order.delivery_date);
          return dd ? dayjs(dd) : null;
        })(),
        purchase_order_no: pick(order.purchaseOrderNo, order.purchase_order_no) || '',
        remark: order.remark || '',
        payment_term: pick(order.paymentTerm, order.payment_term) || ''
      });
    } catch (err) {
      message.error(`${t('commerceSalesOrders.loadDetailFailed')}: ${err.message}`);
    }
  }, [fetchWithAuth, t, currentLanguage]);

  const openCreate = () => {
    setDrawerMode('create');
    setCurrentOrder(null);
    setCustomerInfo(null);
    setSelectedCustomerId(undefined);
    setOrderCompanyId(isTenantAdmin ? (companyFilter || userInfo.company_id || undefined) : undefined);
    setOrderLines([]);
    setHeaderFields({
      order_date: dayjs(),
      delivery_date: null,
      purchase_order_no: '',
      remark: '',
      payment_term: ''
    });
    setDrawerOpen(true);
  };

  const openDetail = async (record) => {
    setDrawerMode('edit');
    setOrderCompanyId(pick(record?.companyId, record?.company_id) || undefined);
    setDrawerOpen(true);
    await loadOrderDetail(record.id);
  };

  const openCustomerPicker = () => {
    if (isTenantAdmin && !effectiveOrderCompanyId) {
      message.warning(t('commerceSalesOrders.selectCompanyFirst'));
      return;
    }
    setPickerPage(1);
    setPickerKeyword('');
    setPickerSearchInput('');
    setCustomerPickerOpen(true);
  };

  const applyPickedCustomer = useCallback((row) => {
    const mapped = mapCustomerFromApi(row, currentLanguage);
    if (!mapped?.id) {
      message.error(t('commerceSalesOrders.customerRequired'));
      return;
    }
    setCustomerInfo(mapped);
    setSelectedCustomerId(mapped.id);
    setCustomerPickerOpen(false);
    // 若客戶有付款條款且訂單尚未填寫，自動帶入（可再修改）
    if (mapped.payment_term) {
      setHeaderFields(prev => ({
        ...prev,
        payment_term: prev.payment_term || mapped.payment_term
      }));
    }
  }, [t, currentLanguage]);

  const clearPickedCustomer = () => {
    setSelectedCustomerId(undefined);
    setCustomerInfo(null);
  };

  const onOrderCompanyChange = (companyId) => {
    setOrderCompanyId(companyId || undefined);
    // 換公司後清空已選客戶，避免 company/customer 不一致
    setSelectedCustomerId(undefined);
    setCustomerInfo(null);
  };

  const selectedCustomerLabel = useMemo(() => {
    if (!customerInfo) return '';
    const branch = [customerInfo.customer_no, customerInfo.branch_no].filter(Boolean).join(' / ');
    const name =
      customerInfo.customer_display_ui
      || pickOrderCustomerNameSnapshot(customerInfo.company_name_cn, customerInfo.company_name_en, currentLanguage);
    return [branch, name].filter(Boolean).join(' — ');
  }, [customerInfo, currentLanguage]);

  const customerPickerColumns = useMemo(() => [
    {
      title: t('commerceSalesOrders.customerNo'),
      key: 'customerNo',
      width: 108,
      render: (_, r) => pick(r.customerNo, r.customer_no) || '-'
    },
    {
      title: t('commerceSalesOrders.branchNo'),
      key: 'branchNo',
      width: 88,
      render: (_, r) => pick(r.branchNo, r.branch_no) || '-'
    },
    {
      title: t('commerceCustomers.customerNameCol'),
      key: 'customerDisplayUi',
      ellipsis: true,
      render: (_, r) => {
        const fromApi = pick(r.displayName, r.DisplayName);
        if (fromApi) return fromApi;
        const n1 = pick(r.name_1, r.name1, r.Name1);
        const n2 = pick(r.name_2, r.name2, r.Name2);
        const n3 = pick(r.name_3, r.name3, r.Name3);
        const nl = parseCustomerNameLocalesRoot(pick(r.name_locales, r.nameLocales, r.NameLocales));
        return resolveCustomerDisplayName(n1, n2, n3, nl, currentLanguage) || '—';
      }
    },
    {
      title: t('commerceSalesOrders.contactName'),
      key: 'contact',
      width: 100,
      ellipsis: true,
      render: (_, r) => pick(r.contactName, r.contact_name) || '-'
    },
    {
      title: t('commerceSalesOrders.contactTel'),
      key: 'tel',
      width: 120,
      render: (_, r) => pick(r.tel, r.contact_tel) || '-'
    },
    {
      title: t('commerceSalesOrders.actions'),
      key: 'pick',
      width: 72,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => applyPickedCustomer(row)}>
          {t('commerceSalesOrders.chooseCustomer')}
        </Button>
      )
    }
  ], [t, applyPickedCustomer, currentLanguage]);

  const productLinePickerColumns = useMemo(() => [
    {
      title: t('commerceSalesOrders.itemNo'),
      key: 'rid',
      width: 140,
      ellipsis: true,
      render: (_, r) => pick(r.productRetailerId, r.productRetailer_id) || '-'
    },
    {
      title: t('commercePriceCards.productNameColUiLocale'),
      key: 'name',
      ellipsis: true,
      render: (_, r) =>
        resolveLocalizedEntityName(r.name, r.nameLocales ?? r.name_locales, currentLanguage) || '—'
    },
    {
      title: t('commerceSalesOrders.productListPrice'),
      key: 'price',
      width: 120,
      render: (_, r) => {
        const price = r.price != null ? Number(r.price) : null;
        const cur = r.currency || 'HKD';
        return price != null ? `${cur} ${price.toFixed(2)}` : '-';
      }
    },
    {
      title: t('commerceSalesOrders.actions'),
      key: 'pick',
      width: 72,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => applyPickedProductFromModal(row)}>
          {t('commerceSalesOrders.chooseProduct')}
        </Button>
      )
    }
  ], [t, applyPickedProductFromModal, currentLanguage]);

  const addOrderLine = () => {
    const nextSeq = orderLines.length > 0 ? Math.max(...orderLines.map(l => l.seq)) + 1 : 1;
    setOrderLines(prev => [
      ...prev,
      {
        key: `new_${Date.now()}`,
        seq: nextSeq,
        item_no: '',
        product_name: '',
        unit_price: 0,
        quantity: 0,
        unit: '',
        amount: 0,
        remark: ''
      }
    ]);
  };

  const removeOrderLine = (key) => {
    setOrderLines(prev => prev.filter(l => l.key !== key));
  };

  const updateOrderLine = (key, field, value) => {
    setOrderLines(prev => prev.map(line => {
      if (line.key !== key) return line;
      const updated = { ...line, [field]: value };
      if (field === 'unit_price' || field === 'quantity') {
        updated.amount = computeBrokenPackLineAmount(
          updated.quantity || 0,
          updated.unit_price || 0,
          updated.qty_per_unit,
          updated.min_order_quantity
        );
      }
      return updated;
    }));
  };

  const totalAmount = useMemo(() => {
    return orderLines.reduce((sum, l) => sum + (l.amount || 0), 0);
  }, [orderLines]);

  const allowedDeliveryDays = useMemo(() => {
    if (!customerInfo) return null;
    const days = new Set();
    DAY_KEYS.forEach(dk => {
      if (customerInfo[`delivery_${dk}`]) days.add(DAY_TO_JS[dk]);
    });
    return days.size > 0 ? days : null;
  }, [customerInfo]);

  const disabledDeliveryDate = useCallback((current) => {
    if (!current || !allowedDeliveryDays) return false;
    return !allowedDeliveryDays.has(current.day());
  }, [allowedDeliveryDays]);

  const saveOrder = async () => {
    if (!selectedCustomerId || !customerInfo) {
      message.error(t('commerceSalesOrders.customerRequired'));
      return;
    }
    if (isTenantAdmin && !effectiveOrderCompanyId) {
      message.error(t('commerceSalesOrders.selectCompanyFirst'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customerId: String(selectedCustomerId),
        customerNo: customerInfo.customer_no || '',
        branchNo: customerInfo.branch_no || '',
        companyNameCn: customerInfo.company_name_cn || '',
        companyNameEn: customerInfo.company_name_en || '',
        billingAddress: customerInfo.billing_address || '',
        deliveryAddress: customerInfo.delivery_address || '',
        contactName: customerInfo.contact_name || '',
        contactTel: customerInfo.contact_tel || '',
        salesmanName: customerInfo.salesman_name || '',
        orderDate: headerFields.order_date ? headerFields.order_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        deliveryDate: headerFields.delivery_date ? headerFields.delivery_date.format('YYYY-MM-DD') : null,
        purchaseOrderNo: headerFields.purchase_order_no || '',
        remark: headerFields.remark || '',
        paymentTerm: headerFields.payment_term || null,
        details: orderLines.map((l) => ({
          productId: l.product_id_guid || null,
          itemNo: String(l.item_no || '').trim().slice(0, 100),
          productName: String(l.product_name || '').trim().slice(0, 200),
          unitPrice: l.unit_price,
          quantity: l.quantity,
          unit: String(l.unit || '').trim().slice(0, 20),
          remark: String(l.remark || '').trim().slice(0, 500)
        }))
      };
      if (isTenantAdmin) {
        const orderCo = pick(currentOrder?.companyId, currentOrder?.company_id);
        if (orderCompanyId || orderCo) payload.companyId = orderCompanyId || orderCo;
      }

      const isEdit = drawerMode === 'edit' && currentOrder?.id;
      const url = isEdit
        ? `/api/commerce/sales-orders/${currentOrder.id}`
        : '/api/commerce/sales-orders';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await fetchWithAuth(url, { method, body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(isEdit ? t('commerceSalesOrders.saved') : t('commerceSalesOrders.created'));
      setDrawerOpen(false);
      loadOrders();
    } catch (err) {
      message.error(`${t('commerceSalesOrders.saveFailed')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/sales-orders/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceSalesOrders.statusUpdated'));
      if (currentOrder?.id === id) {
        await loadOrderDetail(id);
      }
      loadOrders();
    } catch (err) {
      message.error(`${t('commerceSalesOrders.statusUpdateFailed')}: ${err.message}`);
    }
  };

  const deleteOrder = async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/sales-orders/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceSalesOrders.deleted'));
      loadOrders();
    } catch (err) {
      message.error(`${t('commerceSalesOrders.deleteFailed')}: ${err.message}`);
    }
  };

  const renderStatusActions = (order) => {
    if (!order) return null;
    const s = (order.status || '').toLowerCase();
    const actions = [];
    if (s === 'pending') {
      actions.push(
        <Button key="confirm" type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => updateStatus(order.id, 'confirmed')}>
          {t('commerceSalesOrders.confirm')}
        </Button>
      );
      actions.push(
        <Popconfirm key="cancel" title={t('commerceSalesOrders.confirmCancel')} onConfirm={() => updateStatus(order.id, 'cancelled')}>
          <Button size="small" danger icon={<CloseCircleOutlined />}>{t('commerceSalesOrders.cancel')}</Button>
        </Popconfirm>
      );
    }
    if (s === 'confirmed' || s === 'processing') {
      actions.push(
        <Button key="complete" type="primary" size="small" icon={<CheckCircleOutlined />} onClick={() => updateStatus(order.id, 'completed')}>
          {t('commerceSalesOrders.complete')}
        </Button>
      );
      actions.push(
        <Popconfirm key="cancel" title={t('commerceSalesOrders.confirmCancel')} onConfirm={() => updateStatus(order.id, 'cancelled')}>
          <Button size="small" danger icon={<CloseCircleOutlined />}>{t('commerceSalesOrders.cancel')}</Button>
        </Popconfirm>
      );
    }
    return actions.length > 0 ? <Space>{actions}</Space> : null;
  };

  const detailColumns = useMemo(() => {
    const quickProductLabel = (p) => {
      const resolved =
        resolveLocalizedEntityName(p.name, p.nameLocales ?? p.name_locales, currentLanguage) || '';
      const fallback = (p.name || '').trim();
      return (`[${p.productRetailerId}] ${resolved || fallback}`).trim();
    };
    const lineProductSelectOptions = (record) => {
      const seen = new Set();
      const rows = [];
      for (const p of baseQuickProducts) {
        const id = p?.productRetailerId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        rows.push(p);
      }
      const curId = record.item_no;
      if (curId && !seen.has(String(curId))) {
        rows.unshift({
          productRetailerId: String(curId),
          name: record.product_name || '',
          nameLocales: null,
          price: null,
          currency: 'HKD'
        });
      }
      return rows.map((p) => ({
        value: p.productRetailerId,
        label: quickProductLabel(p),
        product: p
      }));
    };

    return [
    {
      title: '#',
      dataIndex: 'seq',
      key: 'seq',
      width: 50,
      render: (_, __, idx) => idx + 1
    },
    {
      title: t('commerceSalesOrders.itemNo'),
      dataIndex: 'item_no',
      key: 'item_no',
      width: 200,
      render: (_, record) => (
        <Select
          size="small"
          showSearch
          allowClear
          style={{ width: '100%', minWidth: 160 }}
          placeholder={t('commerceSalesOrders.searchProductPlaceholder')}
          value={record.item_no || undefined}
          options={lineProductSelectOptions(record)}
          filterOption={(input, option) => {
            const q = (input || '').toLowerCase();
            return (option?.label ?? '').toLowerCase().includes(q);
          }}
          onChange={(val, option) => {
            if (!val) {
              updateOrderLine(record.key, 'item_no', '');
              updateOrderLine(record.key, 'product_name', '');
              return;
            }
            const p = option?.product;
            if (p) applyProductToLine(record.key, p);
          }}
          dropdownRender={(menu) => (
            <>
              {menu}
              <Divider style={{ margin: '4px 0' }} />
              <Button type="link" block size="small" onClick={() => openLineProductPicker(record.key)}>
                {t('commerceSalesOrders.searchMoreProducts')}
              </Button>
            </>
          )}
        />
      )
    },
    {
      title: t('commercePriceCards.productNameColUiLocale'),
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      render: (val, record) => (
        <Input size="small" value={val} onChange={(e) => updateOrderLine(record.key, 'product_name', e.target.value)} />
      )
    },
    {
      title: t('commerceSalesOrders.unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 110,
      render: (val, record) => (
        <InputNumber size="small" min={0} precision={2} style={{ width: '100%' }} value={val} onChange={(v) => updateOrderLine(record.key, 'unit_price', v || 0)} />
      )
    },
    {
      title: t('commerceSalesOrders.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      render: (val, record) => (
        <InputNumber size="small" min={0} precision={2} style={{ width: '100%' }} value={val} onChange={(v) => updateOrderLine(record.key, 'quantity', v || 0)} />
      )
    },
    {
      title: t('commerceSalesOrders.unit'),
      dataIndex: 'unit',
      key: 'unit',
      width: 80,
      render: (val, record) => (
        <Input size="small" value={val} onChange={(e) => updateOrderLine(record.key, 'unit', e.target.value)} />
      )
    },
    {
      title: t('commerceSalesOrders.amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      render: (val) => (val != null ? Number(val).toFixed(2) : '0.00')
    },
    {
      title: t('commerceSalesOrders.lineRemark'),
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      render: (val, record) => (
        <Input size="small" value={val} onChange={(e) => updateOrderLine(record.key, 'remark', e.target.value)} />
      )
    },
    {
      title: t('commerceSalesOrders.actions'),
      key: 'actions',
      width: 70,
      render: (_, record) => (
        <Popconfirm title={t('commerceSalesOrders.confirmDeleteLine')} onConfirm={() => removeOrderLine(record.key)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )
    }
    ];
  }, [t, baseQuickProducts, applyProductToLine, openLineProductPicker, updateOrderLine, currentLanguage]);

  const columns = useMemo(() => [
    {
      title: t('commerceSalesOrders.orderNo'),
      key: 'order_no',
      width: 160,
      render: (_, record) => pick(record.orderNo, record.order_no) || '-'
    },
    {
      title: t('commerceSalesOrders.orderDate'),
      key: 'order_date',
      width: 120,
      render: (_, record) => {
        const val = pick(record.orderDate, record.order_date);
        return val ? formatCommerceDateTime(val, userTimezoneOffset, 'YYYY-MM-DD HH:mm:ss') : '-';
      }
    },
    {
      title: t('commerceSalesOrders.customer'),
      key: 'customer',
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => {
        const customerId = pick(record.customerId, record.customer_id);
        if (!customerId) {
          const waId = pick(record.waId, record.wa_id);
          const fullText = `${t('commerceSalesOrders.anonymousOrder')}${waId ? ` ${waId}` : ''}`.trim();
          return renderEllipsisTooltip(fullText, <Tag color="orange">{fullText}</Tag>);
        }
        const parts = [pick(record.customerNo, record.customer_no), pick(record.branchNo, record.branch_no)].filter(Boolean);
        const joined = parts.join(' / ') || '-';
        return renderEllipsisTooltip(joined, <span>{joined}</span>);
      }
    },
    {
      title: t('commerceCustomers.customerNameCol'),
      key: 'company_display_ui',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const customerId = pick(record.customerId, record.customer_id);
        if (!customerId) {
          const waId = pick(record.waId, record.wa_id);
          return <span style={{ color: '#999' }}>{waId ? `WhatsApp ${waId}` : '-'}</span>;
        }
        return pickOrderCustomerNameSnapshot(
          pick(record.companyNameCn, record.company_name_cn),
          pick(record.companyNameEn, record.company_name_en),
          currentLanguage
        ) || '-';
      }
    },
    {
      title: t('commerceSalesOrders.totalAmount'),
      key: 'total_amount',
      width: 120,
      render: (_, record) => {
        const val = pick(record.totalAmount, record.total_amount);
        return val != null ? Number(val).toFixed(2) : '-';
      }
    },
    {
      title: t('commerceSalesOrders.deliveryDate'),
      key: 'delivery_date',
      width: 120,
      render: (_, record) => {
        const val = pick(record.deliveryDate, record.delivery_date);
        return val ? formatCommerceDateOnly(val, userTimezoneOffset) : '-';
      }
    },
    {
      title: t('commerceSalesOrders.status'),
      key: 'status',
      width: 110,
      render: (_, record) => {
        const val = record.status;
        const s = (val || '').toLowerCase();
        return <Tag color={STATUS_COLOR_MAP[s] || 'default'}>{val || 'unknown'}</Tag>;
      }
    },
    {
      title: t('commerceSalesOrders.source'),
      key: 'source',
      width: 140,
      ellipsis: { showTitle: false },
      render: (_, record) => {
        const val = record.source;
        if (!val) return '-';
        return renderEllipsisTooltip(val, <Tag>{val}</Tag>);
      }
    },
    {
      title: t('commerceSalesOrders.thirdPartyReadStatus'),
      key: 'is_read_by_3psys',
      width: 120,
      render: (_, record) => {
        const v = pick(record.isReadBy3psys, record.is_read_by_3psys);
        const read = v === true || v === 1 || v === '1';
        return read ? (
          <Tag color="green">{t('commerceSalesOrders.thirdPartyReadDone')}</Tag>
        ) : (
          <Tag color="orange">{t('commerceSalesOrders.thirdPartyReadPending')}</Tag>
        );
      }
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset, { format: 'YYYY-MM-DD HH:mm:ss' }),
    {
      title: t('commerceSalesOrders.actions'),
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap={false} size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openDetail(record)}>
            {t('commerceSalesOrders.detail')}
          </Button>
          <Popconfirm title={t('commerceSalesOrders.confirmDelete')} onConfirm={() => deleteOrder(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>{t('commerceSalesOrders.delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [t, currentLanguage, userTimezoneOffset]);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadOrders}>{t('commerceSalesOrders.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('commerceSalesOrders.addOrder')}</Button>
            </Space>
            <div style={{ textAlign: 'right' }}>
              <Title level={3} style={{ margin: 0 }}>{t('commerceSalesOrders.title')}</Title>
              <Text type="secondary">{t('commerceSalesOrders.subtitle')}</Text>
            </div>
          </div>

          <Space wrap>
            <Input.Search
              placeholder={t('commerceSalesOrders.searchPlaceholder')}
              allowClear
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(v) => {
                setKeyword((v ?? '').trim());
                setOrdersPage(1);
              }}
              style={{ width: 280 }}
              prefix={<SearchOutlined />}
            />
            <Select
              allowClear
              placeholder={t('commerceSalesOrders.filterByStatus')}
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v);
                setOrdersPage(1);
              }}
              options={STATUS_OPTIONS}
            />
            <Input
              allowClear
              placeholder={t('commerceSalesOrders.branchNo')}
              value={branchNoFilter}
              onChange={(e) => {
                setBranchNoFilter(e.target.value);
                setOrdersPage(1);
              }}
              style={{ width: 140 }}
            />
            <DatePicker.RangePicker
              value={dateRange}
              placeholder={[t('commerceSalesOrders.deliveryDateFrom') || '交貨日 From', t('commerceSalesOrders.deliveryDateTo') || '交貨日 To']}
              onChange={(v) => {
                setDateRange(v);
                setOrdersPage(1);
              }}
              style={{ width: 280 }}
            />
            {isTenantAdmin && (
              <Select
                allowClear
                placeholder={t('commerceSalesOrders.filterByCompany')}
                style={{ width: 240 }}
                value={companyFilter}
                onChange={(v) => {
                  setCompanyFilter(v);
                  setOrdersPage(1);
                }}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                showSearch
                optionFilterProp="label"
              />
            )}
          </Space>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={orders}
            tableLayout="fixed"
            pagination={commerceServerTablePagination({
              page: ordersPage,
              pageSize: ordersPageSize,
              total: ordersTotal,
              onChange: (p, ps) => {
                setOrdersPage(p);
                setOrdersPageSize(ps);
              }
            })}
            scroll={{ x: 1560, y: 'calc(100vh - 340px)' }}
          />
        </Space>
      </Card>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width="95%"
        title={null}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: 16 }}>
              {t('commerceSalesOrders.totalAmount')}: {totalAmount.toFixed(2)}
            </Text>
            <Space>
              <Button onClick={() => setDrawerOpen(false)}>{t('commerceSalesOrders.close')}</Button>
              <Button type="primary" loading={saving} onClick={saveOrder}>
                {drawerMode === 'create' ? t('commerceSalesOrders.create') : t('commerceSalesOrders.save')}
              </Button>
            </Space>
          </div>
        }
      >
        {/* Header section */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}>
              {drawerMode === 'create'
                ? t('commerceSalesOrders.newOrder')
                : (pick(currentOrder?.orderNo, currentOrder?.order_no) || t('commerceSalesOrders.orderDetail'))}
            </Title>
            {currentOrder?.status && (
              <Tag color={STATUS_COLOR_MAP[(currentOrder.status || '').toLowerCase()] || 'default'} style={{ fontSize: 14, padding: '2px 12px' }}>
                {currentOrder.status}
              </Tag>
            )}
          </Space>
          {drawerMode === 'edit' && currentOrder && renderStatusActions(currentOrder)}
        </div>

        <Divider style={{ margin: '8px 0 16px' }} />

        {/* Customer selector (new / edit 可變更客戶) */}
        {(drawerMode === 'create' || drawerMode === 'edit') && (
          <>
            <Row gutter={16} style={{ marginBottom: 8 }}>
              {isTenantAdmin && (
                <Col span={6}>
                  <Text strong>{t('commerceSalesOrders.company')}</Text>
                  <Select
                    allowClear
                    placeholder={t('commerceSalesOrders.selectCompany')}
                    style={{ width: '100%', marginTop: 4 }}
                    value={orderCompanyId}
                    onChange={onOrderCompanyChange}
                    options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
                    showSearch
                    optionFilterProp="label"
                  />
                </Col>
              )}
              <Col span={isTenantAdmin ? 12 : 18}>
                <Text strong>{t('commerceSalesOrders.selectCustomer')}</Text>
                <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                  <Input
                    readOnly
                    placeholder={t('commerceSalesOrders.selectCustomerPlaceholder')}
                    value={selectedCustomerLabel}
                    title={selectedCustomerLabel}
                  />
                  <Button type="default" icon={<SearchOutlined />} onClick={openCustomerPicker}>
                    {t('commerceSalesOrders.openCustomerList')}
                  </Button>
                  {selectedCustomerId ? (
                    <Button onClick={clearPickedCustomer}>{t('commerceSalesOrders.clearCustomer')}</Button>
                  ) : null}
                </Space.Compact>
              </Col>
              <Col span={6}>
                <Text strong>{t('commerceSalesOrders.orderDate')}</Text>
                <DatePicker
                  style={{ width: '100%', marginTop: 4 }}
                  value={headerFields.order_date}
                  onChange={(d) => setHeaderFields(prev => ({ ...prev, order_date: d }))}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Text strong>{t('commerceSalesOrders.paymentTerm')}</Text>
                <AutoComplete
                  allowClear
                  style={{ width: '100%', marginTop: 4 }}
                  value={headerFields.payment_term || undefined}
                  onChange={(v) => setHeaderFields(prev => ({ ...prev, payment_term: v || '' }))}
                  placeholder={t('commerceSalesOrders.paymentTermPlaceholder')}
                  options={paymentTermOptions.map(v => ({ value: v }))}
                  filterOption={(input, option) =>
                    (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Col>
            </Row>
          </>
        )}

        {/* Customer Info section */}
        {!customerInfo && currentOrder && (pick(currentOrder.waId, currentOrder.wa_id) || pick(currentOrder.customerId, currentOrder.customer_id) == null) && (
          <Descriptions
            title={(
              <Space>
                <span>{t('commerceSalesOrders.customerInfo')}</span>
                <Tag color="orange">{t('commerceSalesOrders.anonymousOrder')}</Tag>
              </Space>
            )}
            bordered
            size="small"
            column={2}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label={t('commerceSalesOrders.waId') || 'WhatsApp ID'} span={2}>
              {pick(currentOrder.waId, currentOrder.wa_id) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('commerceSalesOrders.contactName')}>
              {pick(currentOrder.contactName, currentOrder.contact_name) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('commerceSalesOrders.contactTel')}>
              {pick(currentOrder.contactTel, currentOrder.contact_tel) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('commerceSalesOrders.paymentTerm')}>
              {pick(currentOrder.paymentTerm, currentOrder.payment_term) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('commerceSalesOrders.remark')}>
              {currentOrder.remark || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
        {customerInfo && (
          <>
            <Descriptions
              title={t('commerceSalesOrders.customerInfo')}
              bordered
              size="small"
              column={2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label={t('commerceSalesOrders.customerNo')}>{customerInfo.customer_no || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.branchNo')}>{customerInfo.branch_no || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceCustomers.customerNameCol')} span={2}>
                {customerInfo.customer_display_ui
                  || pickOrderCustomerNameSnapshot(
                    customerInfo.company_name_cn,
                    customerInfo.company_name_en,
                    currentLanguage
                  )
                  || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.billingAddress')} span={2}>{customerInfo.billing_address || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.deliveryAddress')} span={2}>{customerInfo.delivery_address || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.contactName')}>{customerInfo.contact_name || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.contactTel')}>{customerInfo.contact_tel || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('commerceSalesOrders.salesmanName')} span={2}>{customerInfo.salesman_name || '-'}</Descriptions.Item>
            </Descriptions>

            {/* Delivery Schedule */}
            <Card size="small" title={t('commerceSalesOrders.deliverySchedule')} style={{ marginBottom: 16 }}>
              <Space size={16} wrap>
                {DAY_KEYS.map((dk, idx) => (
                  <Checkbox key={dk} checked={!!customerInfo[`delivery_${dk}`]} disabled>
                    {customerInfo[`delivery_${dk}`]
                      ? <span style={{ color: '#52c41a' }}><CheckCircleOutlined /> {DAY_LABELS[idx]}</span>
                      : <span>{DAY_LABELS[idx]}</span>}
                  </Checkbox>
                ))}
                {customerInfo.is_holiday && (
                  <Tag color="volcano">{t('commerceSalesOrders.holidayFlag')}</Tag>
                )}
              </Space>
            </Card>
          </>
        )}

        {/* Delivery date */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Text strong>{t('commerceSalesOrders.deliveryDate')}</Text>
            <DatePicker
              style={{ width: '100%', marginTop: 4 }}
              value={headerFields.delivery_date}
              onChange={(d) => setHeaderFields(prev => ({ ...prev, delivery_date: d }))}
              disabledDate={disabledDeliveryDate}
            />
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0 16px' }}>{t('commerceSalesOrders.orderDetails')}</Divider>

        {/* Editable order details table */}
        <Table
          rowKey="key"
          dataSource={orderLines}
          columns={detailColumns}
          pagination={false}
          size="small"
          scroll={{ x: 1280 }}
          footer={() => (
            <Button type="dashed" block icon={<PlusOutlined />} onClick={addOrderLine}>
              {t('commerceSalesOrders.addLine')}
            </Button>
          )}
        />

        <Divider style={{ margin: '16px 0 12px' }} />

        {/* Footer fields */}
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>{t('commerceSalesOrders.purchaseOrderNo')}</Text>
            <Input
              style={{ marginTop: 4 }}
              value={headerFields.purchase_order_no}
              onChange={(e) => setHeaderFields(prev => ({ ...prev, purchase_order_no: e.target.value }))}
              placeholder={t('commerceSalesOrders.purchaseOrderNoPlaceholder')}
            />
          </Col>
          <Col span={8}>
            <Text strong>{t('commerceSalesOrders.remark')}</Text>
            <Input.TextArea
              style={{ marginTop: 4 }}
              rows={2}
              value={headerFields.remark}
              onChange={(e) => setHeaderFields(prev => ({ ...prev, remark: e.target.value }))}
              placeholder={t('commerceSalesOrders.remarkPlaceholder')}
            />
          </Col>
        </Row>
      </Drawer>

      <Modal
        title={t('commerceSalesOrders.customerPickerTitle')}
        open={customerPickerOpen}
        onCancel={() => setCustomerPickerOpen(false)}
        width={980}
        footer={null}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder={t('commerceSalesOrders.customerPickerSearchPlaceholder')}
          value={pickerSearchInput}
          onChange={(e) => setPickerSearchInput(e.target.value)}
          onSearch={(v) => {
            const q = (v != null ? v : pickerSearchInput).trim();
            setPickerKeyword(q);
            setPickerPage(1);
          }}
          style={{ marginBottom: 12 }}
          enterButton
        />
        <Table
          rowKey={(r, index) => String(pick(r.id, r.Id) || `row_${pickerPage}_${index}`)}
          size="small"
          loading={pickerLoading}
          columns={customerPickerColumns}
          dataSource={pickerRows}
          scroll={{ x: 880 }}
          pagination={commerceServerTablePagination({
            page: pickerPage,
            pageSize: pickerPageSize,
            total: pickerTotal,
            onChange: (p, ps) => {
              setPickerPage(p);
              setPickerPageSize(ps);
            }
          })}
        />
      </Modal>

      <Modal
        title={t('commerceSalesOrders.productPickerTitle')}
        open={productPickerOpen}
        onCancel={() => { setProductPickerOpen(false); setProductPickerLineKey(null); }}
        width={940}
        footer={null}
        destroyOnClose
      >
        <Input.Search
          allowClear
          placeholder={t('commerceSalesOrders.productPickerSearchPlaceholder')}
          value={productPickerSearchInput}
          onChange={(e) => setProductPickerSearchInput(e.target.value)}
          onSearch={(v) => {
            const q = (v != null ? v : productPickerSearchInput).trim();
            setProductPickerKeyword(q);
            setProductPickerPage(1);
          }}
          style={{ marginBottom: 12 }}
          enterButton
        />
        <Table
          rowKey={(r, index) => String(r.id || `p_${productPickerPage}_${index}`)}
          size="small"
          loading={productPickerLoading}
          columns={productLinePickerColumns}
          dataSource={productPickerRows}
          scroll={{ x: 720 }}
          pagination={commerceServerTablePagination({
            page: productPickerPage,
            pageSize: productPickerPageSize,
            total: productPickerTotal,
            onChange: (p, ps) => {
              setProductPickerPage(p);
              setProductPickerPageSize(ps);
            }
          })}
        />
      </Modal>
    </div>
  );
};

export default CommerceSalesOrdersPage;
